import { DOCUMENT } from '@angular/common';
import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Event,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
  RouterOutlet,
} from '@angular/router';
import { filter, take } from 'rxjs';
import { NotificationToastComponent } from './shared/components/notification-toast/notification-toast.component';
import { PageProgressBarComponent } from './shared/components/page-progress-bar/page-progress-bar.component';
import { ChunkReloadService } from './core/services/chunk-reload.service';
import { removeAppShell } from './core/startup/app-shell';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NotificationToastComponent, PageProgressBarComponent],
  templateUrl: './app.component.html',
})
export class AppComponent {
  title = 'trading-ai-frontend';
  private readonly chunkReload = inject(ChunkReloadService);

  constructor() {
    // index.html shows a start-up shell; the first navigation that settles replaces it
    const document = inject(DOCUMENT);
    inject(Router)
      .events.pipe(
        filter((event) => this.settles(event)),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe(() => removeAppShell(document));
  }

  private settles(event: Event): boolean {
    if (event instanceof NavigationEnd || event instanceof NavigationSkipped) return true;
    // A missing chunk reloads the app, or the shell shows the error: it stays
    if (event instanceof NavigationError) return !this.chunkReload.isChunkLoadError(event.error);
    // A redirect (the login guard), or a guard that starts another navigation, is followed
    // by the navigation that settles
    return (
      event instanceof NavigationCancel &&
      event.code !== NavigationCancellationCode.Redirect &&
      event.code !== NavigationCancellationCode.SupersededByNewNavigation
    );
  }
}
